import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { 
  Settings, Clock, Save, RefreshCw, Calendar, Users, MapPin, ChevronDown, ChevronRight, Award, FileText
} from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ExamSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    exam_duration_minutes: 90,
    exam_name: "CRP-EP Examination",
    secondary_language: "Assamese"
  });
  const [examHistory, setExamHistory] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/exam-settings`),
        axios.get(`${API}/admin/exam-history`)
      ]);
      setSettings(settingsRes.data);
      setExamHistory(historyRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/exam-settings`, settings);
      toast.success("Exam settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleDateExpand = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "-";
    const date = new Date(isoStr);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  return (
    <AdminSidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Exam Settings & History</h1>
            <p className="text-sm text-gray-500">Configure exam duration and view exam history</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Exam Settings Card */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-emerald-600" />
                  Exam Configuration
                </CardTitle>
                <CardDescription>
                  Set the exam time limit and name
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="exam_name">Exam Name</Label>
                  <Input
                    id="exam_name"
                    value={settings.exam_name}
                    onChange={(e) => setSettings({ ...settings, exam_name: e.target.value })}
                    placeholder="Enter exam name"
                    data-testid="exam-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_language">Secondary Language</Label>
                  <Input
                    id="secondary_language"
                    value={settings.secondary_language || ""}
                    onChange={(e) => setSettings({ ...settings, secondary_language: e.target.value })}
                    placeholder="e.g., Assamese, Manipuri, Hindi, Bengali"
                    data-testid="secondary-language-input"
                  />
                  <p className="text-xs text-gray-500">
                    Used as the regional language label in question forms, exam UI, and instructions. Existing question text remains unchanged.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exam_duration">Time Limit (Minutes)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="exam_duration"
                      type="number"
                      min="10"
                      max="300"
                      value={settings.exam_duration_minutes}
                      onChange={(e) => setSettings({ ...settings, exam_duration_minutes: parseInt(e.target.value) || 90 })}
                      className="w-24"
                      data-testid="exam-duration-input"
                    />
                    <span className="text-sm text-gray-500">
                      = {Math.floor(settings.exam_duration_minutes / 60)}h {settings.exam_duration_minutes % 60}m
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Set between 10 and 300 minutes
                  </p>
                </div>
                
                {/* Quick Time Presets */}
                <div className="space-y-2">
                  <Label>Quick Presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {[30, 60, 90, 120, 180].map((mins) => (
                      <Button
                        key={mins}
                        variant={settings.exam_duration_minutes === mins ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSettings({ ...settings, exam_duration_minutes: mins })}
                        className={settings.exam_duration_minutes === mins ? "bg-emerald-600" : ""}
                      >
                        {mins} min
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-settings-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Settings"}
                </Button>

                {/* Current Settings Display */}
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Current Time Limit</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">
                    {settings.exam_duration_minutes} minutes
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    ({Math.floor(settings.exam_duration_minutes / 60)} hours {settings.exam_duration_minutes % 60} minutes)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Exam History Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Exam History
                </CardTitle>
                <CardDescription>
                  View past exams by date, block, and candidates
                </CardDescription>
              </CardHeader>
              <CardContent>
                {examHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No exam history yet</p>
                    <p className="text-sm">Exam records will appear here after candidates submit</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-4">
                      {examHistory.map((dayData) => (
                        <Collapsible
                          key={dayData.date}
                          open={expandedDates[dayData.date]}
                          onOpenChange={() => toggleDateExpand(dayData.date)}
                        >
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                {expandedDates[dayData.date] ? (
                                  <ChevronDown className="w-5 h-5 text-gray-500" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-500" />
                                )}
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {formatDate(dayData.date)}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {dayData.total_blocks} blocks participated
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                  <Users className="w-3 h-3 mr-1" />
                                  {dayData.total_candidates} candidates
                                </Badge>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="mt-2 ml-8 space-y-3">
                              {dayData.blocks.map((blockData) => (
                                <Collapsible key={blockData.block}>
                                  <CollapsibleTrigger asChild>
                                    <div className="flex items-center justify-between p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
                                      <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-emerald-600" />
                                        <div>
                                          <p className="font-medium">{blockData.block}</p>
                                          <p className="text-xs text-gray-500">
                                            {blockData.district}, {blockData.state}
                                          </p>
                                        </div>
                                      </div>
                                      <Badge className="bg-emerald-100 text-emerald-700">
                                        {blockData.count} candidates
                                      </Badge>
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="mt-2 ml-6">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Reg. No.</TableHead>
                                            <TableHead className="text-center">Score</TableHead>
                                            <TableHead className="text-center">Status</TableHead>
                                            <TableHead className="text-center">Time</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {blockData.candidates.map((candidate) => (
                                            <TableRow key={candidate.id}>
                                              <TableCell className="font-medium">
                                                {candidate.name}
                                              </TableCell>
                                              <TableCell className="text-gray-600">
                                                {candidate.registration_number}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <span className="font-semibold">
                                                  {candidate.total_score || 0}
                                                </span>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {candidate.qualified ? (
                                                  <Badge className="bg-green-100 text-green-700">
                                                    <Award className="w-3 h-3 mr-1" />
                                                    Qualified
                                                  </Badge>
                                                ) : (
                                                  <Badge variant="outline" className="text-gray-500">
                                                    Not Qualified
                                                  </Badge>
                                                )}
                                              </TableCell>
                                              <TableCell className="text-center text-sm text-gray-500">
                                                {formatTime(candidate.submitted_at)}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminSidebar>
  );
}
